import type { ItemUI } from '@libs/common-node';
import { NavigationListNames, NavigationListTypes, getOrigin, getQueryStringValue } from '@libs/common-next';

import type { ItemsListItem } from '@libs/common-next';
import type { ProductHit } from '@libs/algolia';
import { StockStatusEnum } from '@petsmart-ui/sparky';
import { getDefaultCategories } from '@libs/algolia';
import { joinUrlOrPath } from '@libs/core';
import { TrackingParams } from '../constants';

export const getProductObject = (product: ProductHit) => ({
    name: product.name,
    id: product.objectID,
    brand: product.brand,
    dimension5: product['pharmacy-type'] || 'Other',
    dimension18: product.color?.value,
    dimension19: product.size ? Object.values(product.size)[0] : undefined,
    dimension20: product.flavor?.value,
    dimension21: product.customCaseQuantity,
    dimension41: product.merchandisingHierarchy?.vicePresidentName,
    dimension42: product.merchandisingHierarchy?.division,
    dimension43: product.merchandisingHierarchy?.department,
    dimension44: product.merchandisingHierarchy?.class,
    dimension45: product.merchandisingHierarchy?.category,
});

export const mapProductClick = (product: ProductHit) => {
    const price = product.price?.number || (product.prices ? Number(product.prices[0]) : undefined);
    return {
        name: product.name || product.title,
        id: product.objectID,
        brand: product.brand,
        price,
        dimension5: product['pharmacy-type'] || 'Other',
        dimension6: TrackingParams.STANDARD_PDP,
        dimension41: product.merchandisingHierarchy?.vicePresidentName,
        dimension42: product.merchandisingHierarchy?.division,
        dimension43: product.merchandisingHierarchy?.department,
        dimension44: product.merchandisingHierarchy?.class,
        dimension45: product.merchandisingHierarchy?.category,
    };
};

export const getAvailabilityConstant = (ats: number) => (ats > 0 ? TrackingParams.YES : TrackingParams.NO);

export const getStockLevelConstant = (stockLevel: StockStatusEnum) =>
    ({
        [StockStatusEnum.InStock]: 'In Stock',
        [StockStatusEnum.FewLeftInStock]: 'Low Stock',
        [StockStatusEnum.OutOfStock]: 'Out of Stock',
        [StockStatusEnum.NotAvailable]: null,
    })[stockLevel];

export const getDimensionCategories = (product: ProductHit) => {
    const categories = getDefaultCategories(product);
    return categories?.length
        ? {
              dimension9: categories[0]?.name.default ?? null,
              dimension10: categories[1]?.name.default ?? null,
              dimension11: categories[2]?.name.default ?? null,
          }
        : {};
};

export const mapImpressions = (products: ProductHit[], list: string) =>
    products.map(product => ({
        list,
        price: product.price?.number,
        position: product.__position,
        ...getDimensionCategories(product),
        ...getProductObject(product),
    }));

export const getBaseUrlJoined = (path: string | undefined) =>
    path ? joinUrlOrPath(window.location.origin, path) : path;

export const getSubmitSearchType = (searchAssistName?: string) =>
    searchAssistName ? 'assisted search' : 'manual search';

export const getViewSearchResultsType = (numberOfResults: number, searchType?: string) =>
    numberOfResults === 0 ? 'no results' : searchType === 'manual' ? 'manual search' : 'assisted search';

export const getNavigationName = (findingMethod: string) => {
    switch (findingMethod) {
        case TrackingParams.RECOMMENDATION_REFERER:
            return NavigationListNames.PRODUCT_RECOMMENDATION;
        case TrackingParams.HOMEPAGE_REFERER:
            return NavigationListNames.HOMEPAGE;
        case TrackingParams.BROWSE_REFERER:
            return NavigationListNames.MAIN;
        case TrackingParams.SEARCH_REFERER:
            return NavigationListNames.SEARCH;
        case TrackingParams.CONTENTFUL_REFERER:
            return NavigationListNames.CONTENTFUL;
        default:
            return NavigationListNames.MAIN;
    }
};

export const getNavigationId = (findingMethod: string) => {
    switch (findingMethod) {
        case TrackingParams.RECOMMENDATION_REFERER:
            return NavigationListTypes.PRODUCT_RECOMMENDATION;
        case TrackingParams.HOMEPAGE_REFERER:
            return NavigationListTypes.HOMEPAGE;
        case TrackingParams.BROWSE_REFERER:
            return NavigationListTypes.STANDARD_PLP;
        case TrackingParams.SEARCH_REFERER:
            return NavigationListTypes.SEARCH;
        case TrackingParams.CONTENTFUL_REFERER:
            return NavigationListTypes.CONTENTFUL;
        default:
            return NavigationListTypes.STANDARD_PDP;
    }
};

export const processFindingMethod = (findingMethod = 'Other') => {
    if (document.referrer) {
        const referrer = new URL(document.referrer);

        if (referrer.origin !== getOrigin()) {
            return TrackingParams.EXTERNAL_REFERER;
        }
    } else {
        return TrackingParams.EXTERNAL_REFERER;
    }

    // This one is used to support the method being used in the legacy app
    const fmethod = getQueryStringValue('fmethod');
    if (fmethod) {
        return fmethod;
    }

    // This code block is used to support the content slots on homepage
    // in the legacy app.
    const { previousPage } = window.sessionStorage;
    if (previousPage === 'homepage') {
        return TrackingParams.HOMEPAGE_REFERER;
    }

    // And lastly, for the new way of tracking this kind of event:
    return findingMethod;
};

export const getPromoOrigin = (pathname?: string | null) =>
    pathname !== '/' ? pathname?.substring(1).replace(/\//g, '-') : 'homepage';

export const getPromoDesc = (desc?: string) => desc?.toLowerCase().replace(/\s+/g, '-') ?? '';

export const mapCategoriesFromUrl = (pathname: string | null) => pathname?.split('/').filter(item => item !== '') || [];

export const mapCartItem = (item: ItemsListItem) => ({
    name: item.name,
    id: item.sku,
    price: item.totalPrice / 100,
    brand: item.merchandiseBrand,
    quantity: item.quantity,
    dimension18: item.variant?.color ?? null, // Color of the item; null if not applicable
    dimension19: item.variant?.size ?? null,
    dimension20: item.variant?.flavor ?? null, // Flavor of the item; null if not applicable
    dimension5: item.pharmacyType ?? 'Others', // Product type, options include Others, Vet Diet, Pharmacy
    dimension21: item.customCaseQuantity ?? null,
    dimension12: item.fulfillmentMethod?.toLowerCase() ?? null,
    dimension14: item.storeId ?? null,
    dimension13: item.autoshipFrequency ?? null,
    dimension39: 'Yes',
    dimension41: item.mhVicePresidentName,
    dimension42: item.mhDivision,
    dimension43: item.mhDepartment,
    dimension44: item.mhClass,
    dimension45: item.mhCategory,
    metric4: item.basePrice / 100,
    metric5: item.salePrice / 100,
    maxQtyReached: item.quantity < item.availableQuantity ? 'no' : 'yes',
    dimension31: item.positionWithinCart,
});

export const mapItem = (item: ItemUI) => ({
    name: item.name,
    id: item.sku,
    price: item.totalPrice,
    brand: item.merchandiseBrand,
    quantity: item.quantity,
    dimension18: item.variant?.color ?? null, // Color of the item; null if not applicable
    dimension19: item.variant?.size ?? null,
    dimension20: item.variant?.flavor ?? null, // Flavor of the item; null if not applicable
    dimension5: item.pharmacyType ?? 'Others', // Product type, options include Others, Vet Diet, Pharmacy
    dimension21: item.customCaseQuantity ?? null,
    dimension12: null,
    dimension14: null,
    dimension13: item.autoshipFrequency ?? null,
    dimension39: 'Yes',
    dimension41: item.mhVicePresidentName,
    dimension42: item.mhDivision,
    dimension43: item.mhDepartment,
    dimension44: item.mhClass,
    dimension45: item.mhCategory,
    metric4: item.basePrice,
    metric5: item.salePrice,
    maxQtyReached: item.quantity < item.availableQuantity ? 'no' : 'yes',
    dimension31: item.positionWithinCart,
});
